import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { createCustomer, updateCustomer, getCustomer } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { SavingOverlay } from '../../core/public/SavingOverlay';

const CustomerFormPage = () => {
  const { translate } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    address: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  const loadCustomer = useCallback(async () => {
    try {
      const response = await getCustomer(id);
      if (response.data) {
        setFormData({
          name: response.data.name || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          document: response.data.document || '',
          address: response.data.address || '',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadCustomer();
    }
  }, [isEdit, loadCustomer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = translate('customers.name_required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email?.trim() || '',
        phone: formData.phone?.trim() || '',
        document: formData.document?.trim() || '',
        address: formData.address?.trim() || '',
      };

      if (isEdit) {
        await updateCustomer(id, payload);
        setSnackbar({ open: true, message: translate('customers.updated'), severity: 'success' });
      } else {
        await createCustomer(payload);
        setSnackbar({ open: true, message: translate('customers.created'), severity: 'success' });
      }
      navigate('/customers');
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label={translate('customers.name')}
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label={translate('customers.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            fullWidth
            margin="normal"
            label={translate('customers.phone')}
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label={translate('customers.document')}
            name="document"
            value={formData.document}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label={translate('customers.address')}
            name="address"
            multiline
            rows={2}
            value={formData.address}
            onChange={handleChange}
          />
          <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button type="submit" variant="contained" disabled={loading} fullWidth={false} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {loading ? translate('common.saving') : translate('common.save')}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/customers')} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {translate('common.cancel')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <SavingOverlay open={loading} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerFormPage;
